import { useEffect, useRef, useState } from "react";
import type { LabModule, LabTheme, LabValues } from "@/lib/labs/types";

export function useThemeInk() {
  const probe = useRef<HTMLDivElement>(null);
  const [colors, setColors] = useState<LabTheme>({
    ink: "#e8eaf6",
    muted: "#8b93a7",
    accent: "#818cf8",
    surface: "#1a1c25",
  });
  useEffect(() => {
    const el = probe.current;
    if (!el) return;
    const read = (cls: string) => {
      const span = el.querySelector<HTMLElement>(`[data-c="${cls}"]`);
      return span ? getComputedStyle(span).color : undefined;
    };
    setColors((c) => ({
      ink: read("ink") ?? c.ink,
      muted: read("muted") ?? c.muted,
      accent: read("accent") ?? c.accent,
      surface: read("surface") ?? c.surface,
    }));
  }, []);
  const node = (
    <div ref={probe} aria-hidden className="pointer-events-none absolute size-0 overflow-hidden">
      <span data-c="ink" className="text-foreground" />
      <span data-c="muted" className="text-muted-foreground" />
      <span data-c="accent" className="text-primary" />
      <span data-c="surface" className="text-surface" />
    </div>
  );
  return { colors, node };
}

type StageProps = {
  lab: LabModule<unknown>;
  values: LabValues;
  playing: boolean;
  zoom: number;
  resetKey: number;
  height: number;
  /** receives the live state object so the shell can read readouts / challenge */
  onState: (state: unknown) => void;
  reducedMotion: boolean;
};

/**
 * Canvas host: owns the rAF loop, real-time stepping and direct pointer
 * manipulation (drag handles / taps) so labs stay pure logic modules.
 */
export function LabStage({
  lab,
  values,
  playing,
  zoom,
  resetKey,
  height,
  onState,
  reducedMotion,
}: StageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { colors, node } = useThemeInk();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [dragging, setDragging] = useState<string | null>(null);
  const draggingRef = useRef<string | null>(null);
  draggingRef.current = dragging;
  const stateRef = useRef<unknown>(null);
  const sizeRef = useRef({ width: 320, height });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let last = performance.now();
    let t = 0;
    let size = { width: canvas.clientWidth || 320, height: canvas.clientHeight || height };
    let state: unknown;
    try {
      state = lab.init(valuesRef.current, size);
    } catch {
      state = {};
    }
    onState(state);
    stateRef.current = state;
    sizeRef.current = size;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      size = { width: rect.width, height: rect.height };
      sizeRef.current = size;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const frame = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const z = zoomRef.current;
      if (playingRef.current) {
        t += dt;
        try {
          lab.step?.(state, reducedMotion ? Math.min(dt, 1 / 60) : dt, valuesRef.current, size);
        } catch {
          /* keep the loop alive on a bad parameter combination */
        }
      }
      ctx.save();
      ctx.clearRect(0, 0, size.width, size.height);
      ctx.translate(size.width / 2, size.height / 2);
      ctx.scale(z, z);
      ctx.translate(-size.width / 2, -size.height / 2);
      try {
        lab.render({
          ctx,
          width: size.width,
          height: size.height,
          t,
          state,
          p: valuesRef.current,
          theme: colors,
          dragging: draggingRef.current,
        });
      } catch {
        /* never crash the page on one bad frame */
      }
      ctx.restore();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    // Stop burning CPU/battery while the tab is in the background.
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab, colors, resetKey]);

  const toLocal = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const z = zoomRef.current || 1;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    return {
      x: cx + (e.clientX - rect.left - cx) / z,
      y: cy + (e.clientY - rect.top - cy) / z,
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const s = stateRef.current;
    if (!s) return;
    const { x, y } = toLocal(e);
    let picked: string | null = null;
    try {
      const handles = lab.handles?.(s, valuesRef.current, sizeRef.current) ?? [];
      let best = Infinity;
      for (const h of handles) {
        const d = Math.hypot(h.x - x, h.y - y);
        if (d <= (h.r ?? 26) && d < best) {
          best = d;
          picked = h.id;
        }
      }
    } catch {
      picked = null;
    }
    if (picked) {
      setDragging(picked);
      draggingRef.current = picked;
      e.currentTarget.setPointerCapture(e.pointerId);
      try {
        lab.onDrag?.(s, picked, x, y, valuesRef.current, sizeRef.current);
      } catch {
        /* ignore */
      }
    } else {
      try {
        lab.onTap?.(s, x, y, valuesRef.current, sizeRef.current);
      } catch {
        /* ignore */
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const id = draggingRef.current;
    const s = stateRef.current;
    if (!id || !s) return;
    e.preventDefault();
    const { x, y } = toLocal(e);
    try {
      lab.onDrag?.(s, id, x, y, valuesRef.current, sizeRef.current);
    } catch {
      /* ignore */
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const id = draggingRef.current;
    if (!id) return;
    const s = stateRef.current;
    if (s) {
      try {
        lab.onDrop?.(s, id, valuesRef.current, sizeRef.current);
      } catch {
        /* ignore */
      }
    }
    setDragging(null);
    draggingRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative">
      {node}
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: "none" }}
        className="w-full rounded-[18px]"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
    </div>
  );
}